import React, { useState, useRef } from "react";
import { Helmet } from "react-helmet";
import { useLocation } from "react-router-dom";
import { ClipboardList, Check } from "lucide-react";
import "./SummaryGenerator.css";
import summaryIcon from "../../assets/OtherTools/summary.png";
import Footer from "../../components/Footer/Footer";
import ReadyToStartBox from "../../components/ReadyToStartBox/ReadyToStartBox";
import Navbar from "../../components/Navbar/Navbar";
import DashboardSidebar from "../../components/DashboardSidebar/DashboardSidebar";
import useAuthStore from "../../stores/authStore";

export default function SummaryGenerator() {
  const { user } = useAuthStore();
  const location = useLocation();
  const isDashboardLayout = location.pathname.startsWith("/dashboard");
  const API_URL = process.env.REACT_APP_API_URL;

  const [role, setRole] = useState("");
  const [audience, setAudience] = useState("");
  const [cta, setCta] = useState("");
  const [tone, setTone] = useState("Formal");
  const [useEmojis, setUseEmojis] = useState("Yes");

  // refs for inputs
  const roleRef = useRef(null);
  const audienceRef = useRef(null);
  const ctaRef = useRef(null);
  const toneSelectRef = useRef(null);
  const emojiSelectRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState("");
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const handleGenerate = async (e) => {
  e.preventDefault();

  if (!role.trim() || !audience.trim() || !cta.trim()) {
    setError("Please fill in all required fields before generating a summary.");
    return;
  }

  const payload = {
    role,
    target: audience,
    cta,
    tone,
    include_emojis: useEmojis === "Yes",
  };

  setLoading(true);
  setError("");
  setSummary("");

  let attempt = 0;
  let success = false;

  while (attempt < 2 && !success) {
    try {
      const response = await fetch(`${API_URL}/api/public/linkedin-summary`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        credentials: "include",
      });

      if (!response.ok) {
        const data = await response.json();
        if (response.status === 422 && Array.isArray(data.detail)) {
          setError(
            `Validation error: ${data.detail.map((d) => d.msg).join(", ")}`
          );
        } else {
          throw new Error(data?.error || "Failed to generate summary.");
        }
      } else {
        const data = await response.json();
        setSummary(data.summary || "No summary generated.");
        success = true;
      }
    } catch (err) {
      attempt++;
      if (attempt >= 2) {
        console.error(err);
        setError("Servers are busy, please try again");
      }
    }
  }

  setLoading(false);
};


  const handleCopy = () => {
    if (!summary) return;
    navigator.clipboard.writeText(summary);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <>
      {isDashboardLayout ? (
        <DashboardSidebar userName={user?.name} profilePic={user?.profilePic} />
      ) : (
        <Navbar />
      )}

      <Helmet>
        <title>Generate LinkedIn Summaries | Free AI Summary Generator</title>
      </Helmet>

      <header className="sg-hero">
        <div className="sg-icon">
          <img src={summaryIcon} alt="Summary icon" className="sg-icon-img" />
        </div>
        <h1>
          Generate LinkedIn <span className="sg-highlight">Summaries</span>
        </h1>
        <p className="sg-subtitle">
          Tell us about yourself and Brandly will craft the perfect summary that captures who you are.
        </p>

        <form className="sg-form" onSubmit={handleGenerate}>
          {/* Role */}
          <div
            className={`sg-input-pill ${role ? "active" : ""}`}
            onClick={() => roleRef.current?.focus()}
          >
            <span id="role-label" className="sg-pill-label">
              Your Role
            </span>

            <div className="sg-input-wrapper">
              <input
                ref={roleRef}
                aria-labelledby="role-label"
                type="text"
                value={role}
                onChange={(e) => setRole(e.target.value)}
                onFocus={(e) => e.currentTarget.classList.add("focused")}
                onBlur={(e) => e.currentTarget.classList.remove("focused")}
                className="sg-pill-input"
                required
              />
              {!role && (
                <span className="sg-custom-placeholder">
                  e.g., Growth strategist helping startups scale
                </span>
              )}
            </div>
          </div>

          {/* Audience */}
          <div
            className={`sg-input-pill ${audience ? "active" : ""}`}
            onClick={() => audienceRef.current?.focus()}
          >
            <span id="audience-label" className="sg-pill-label">
              Target Audience
            </span>

            <div className="sg-input-wrapper">
              <input
                ref={audienceRef}
                aria-labelledby="audience-label"
                type="text"
                value={audience}
                onChange={(e) => setAudience(e.target.value)}
                onFocus={(e) => e.currentTarget.classList.add("focused")}
                onBlur={(e) => e.currentTarget.classList.remove("focused")}
                className="sg-pill-input"
                required
              />
              {!audience && (
                <span className="sg-custom-placeholder">
                  e.g., Tech founders, marketing teams
                </span>
              )}
            </div>
          </div>

          {/* CTA */}
          <div
            className={`sg-input-pill ${cta ? "active" : ""}`}
            onClick={() => ctaRef.current?.focus()}
          >
            <span id="cta-label" className="sg-pill-label">
              Call to Action
            </span>

            <div className="sg-input-wrapper">
              <input
                ref={ctaRef}
                aria-labelledby="cta-label"
                type="text"
                value={cta}
                onChange={(e) => setCta(e.target.value)}
                onFocus={(e) => e.currentTarget.classList.add("focused")}
                onBlur={(e) => e.currentTarget.classList.remove("focused")}
                className="sg-pill-input"
                required
              />
              {!cta && (
                <span className="sg-custom-placeholder">
                  e.g., Connect with me, Learn more
                </span>
              )}
            </div>
          </div>

          {/* Tone + Emojis (untouched dropdown pills) */}
          <div className="sg-form-row">
            <div
              className={`sg-input-pill ${tone ? "active" : ""}`}
              onClick={() => toneSelectRef.current?.focus()}
            >
              <span id="tone-label" className="sg-pill-label">Tone of Voice</span>
              <select
                ref={toneSelectRef}
                aria-labelledby="tone-label"
                value={tone}
                onChange={(e) => setTone(e.target.value)}
                onFocus={(e) => e.currentTarget.classList.add("active")}
                onBlur={(e) => {
                  if (!e.target.value) e.currentTarget.classList.remove("active");
                }}
                className="sg-pill-input sg-pill-select"
              >
                <option value="Formal">Formal</option>
                <option value="Casual">Casual</option>
                <option value="Friendly">Friendly</option>
              </select>
            </div>

            <div
              className={`sg-input-pill ${useEmojis ? "active" : ""}`}
              onClick={() => emojiSelectRef.current?.focus()}
            >
              <span id="emoji-label" className="sg-pill-label">Use Emojis?</span>
              <select
                ref={emojiSelectRef}
                aria-labelledby="emoji-label"
                value={useEmojis}
                onChange={(e) => setUseEmojis(e.target.value)}
                onFocus={(e) => e.currentTarget.classList.add("active")}
                onBlur={(e) => {
                  if (!e.target.value) e.currentTarget.classList.remove("active");
                }}
                className="sg-pill-input sg-pill-select"
              >
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>
          </div>

          <div style={{ textAlign: "center", marginTop: "1rem" }}>
            <button type="submit" className="sg-generate-btn" disabled={loading}>
              {loading ? "Generating..." : "Generate Summary"}
            </button>
          </div>
        </form>

        {error && <p className="sg-error-message">{error}</p>}

        {summary && (
          <div className="sg-summary-box">
            <p className="sg-summary-text">{summary}</p>
            <button className="sg-copy-btn" onClick={handleCopy} title="Copy">
              {copied ? <Check size={18} /> : <ClipboardList size={18} />}
            </button>
          </div>
        )}

        {summary && (
          <div className="sg-summary-actions">
            <button className="sg-action-btn" onClick={() => setSummary("")}>
              New Summary +
            </button>
            <button className="sg-action-btn" onClick={handleGenerate}>
              Generate Again ⟳
            </button>
          </div>
        )}
      </header>

      {!isDashboardLayout && <ReadyToStartBox />}
      <Footer />
    </>
  );
}
